import { Injectable } from '@angular/core';
import { WindowRef } from '../../../util/window.ref';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class SessionStorageService {
  private sessionStorage = WindowRef.nativeWindow.sessionStorage;
  private subjects: Map<string, BehaviorSubject<any>>;

  constructor() {
    this.subjects = new Map<string, BehaviorSubject<any>>();
  }

  /**
   * Returns the current value associated with the given key as an Observable,
   * or creates a new key/value pair if none existed for key previously
   *
   * @param key - Session Storage Item
   * @return - Fetched Item's value
   */
  getItem$<T>(key: string): Observable<T | null> {
    const item = this.sessionStorage.getItem(key);
    if (!this.subjects.has(key)) {
      this.subjects.set(key, new BehaviorSubject<T>(JSON.parse(item) as T));
    } else {
      this.subjects.get(key).next(JSON.parse(item) as T);
    }
    return this.subjects.get(key).asObservable();
  }

  /**
   * Returns the current value associated with the given key,
   * or 'false' if the given key/value does not exist in the list associated with the object.
   *
   * @param key - Session Storage Item key
   * @return - Fetched Item's value
   */
  getItem<T>(key: string): T | boolean {
    try {
      const item = this.sessionStorage.getItem(key);
      return JSON.parse(item) as T;
    } catch {
      return false;
    }
  }

  /**
   * Sets the value of the pair identified by key to value,
   * creating a new key/value pair if none existed for key previously.
   *
   * @param key - Session Storage key
   * @param value - Session Storage key's value
   * @return - Saved status
   */
  setItem(key: string, value: any): boolean {
    try {
      this.sessionStorage.setItem(key, JSON.stringify(value));
      if (!this.subjects.has(key)) {
        this.subjects.set(key, new BehaviorSubject<any>(value));
      } else {
        this.subjects.get(key).next(value);
      }
      return true;
    } catch {
      return false;
    }
  }
}
